/**
 * Asset-origin picker. Shared between the Project Settings dialog and
 * the New Project dialog so both surfaces show the same affordance for
 * configuring where image-widget Browse pulls from.
 *
 * The "None" option means no asset origin — the image widget falls
 * back to a plain URL input with no gallery affordance.
 *
 * Credentials live in browser localStorage alongside the rest of the
 * project config, same trade-off as the GitHub PAT and Cloudflare KV
 * token. The paid centralized version moves them server-side.
 */

import type { AssetOriginConfig } from '@bliksys/shared';
import { useId, useState } from 'react';

import { AssetOriginError, testConnection } from '../asset-origin.js';

import { LocalCredentialNotice } from './LocalCredentialNotice.js';

interface Props {
  origin: AssetOriginConfig | undefined;
  onChange: (o: AssetOriginConfig | undefined) => void;
}

export function AssetOriginField({ origin, onChange }: Props): JSX.Element {
  const kindId = useId();
  const value = origin?.kind ?? 'none';
  return (
    <>
      <p className="project-settings-section-hint">
        Where image-widget Browse pulls from. Any S3-compatible service works (AWS S3, Cloudflare
        R2, Backblaze B2, MinIO).
      </p>
      <div className="landing-field">
        <label htmlFor={kindId} className="sr-only">
          Origin kind
        </label>
        <select
          id={kindId}
          className="input"
          value={value}
          onChange={(e) => {
            const kind = e.target.value;
            if (kind === 'none') onChange(undefined);
            else if (kind === 's3')
              onChange(
                origin?.kind === 's3'
                  ? origin
                  : {
                      kind: 's3',
                      endpoint: '',
                      bucket: '',
                      region: '',
                      accessKeyId: '',
                      secretAccessKey: '',
                    },
              );
          }}
        >
          <option value="none">None — hide gallery, use plain URL input</option>
          <option value="s3">S3-compatible (AWS, R2, MinIO, B2, ...)</option>
        </select>
      </div>

      {origin?.kind === 's3' && <S3Fields origin={origin} onChange={(o) => onChange(o)} />}
    </>
  );
}

/**
 * Friendly label for an asset-origin kind. Used by the settings
 * dialog's collapsed-section badge.
 */
export function labelAssetKind(kind: AssetOriginConfig['kind']): string {
  switch (kind) {
    case 's3':
      return 'S3-compatible';
  }
}

function S3Fields({
  origin,
  onChange,
}: {
  origin: Extract<AssetOriginConfig, { kind: 's3' }>;
  onChange: (o: Extract<AssetOriginConfig, { kind: 's3' }>) => void;
}): JSX.Element {
  const endpointId = useId();
  const bucketId = useId();
  const regionId = useId();
  const accessId = useId();
  const secretId = useId();
  const publicId = useId();
  const prefixId = useId();
  const pathStyleId = useId();

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const handleTest = async (): Promise<void> => {
    setTesting(true);
    setTestResult(null);
    try {
      await testConnection(origin);
      setTestResult({ ok: true, message: 'Connected. Bucket is reachable.' });
    } catch (err) {
      const message =
        err instanceof AssetOriginError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unknown error.';
      setTestResult({ ok: false, message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <LocalCredentialNotice />
      <div className="landing-field">
        <label className="landing-label" htmlFor={endpointId}>
          Endpoint{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            e.g. <code>https://s3.us-east-1.amazonaws.com</code>,{' '}
            <code>https://&lt;account&gt;.r2.cloudflarestorage.com</code>, or{' '}
            <code>http://localhost:9000</code> for MinIO.
          </span>
        </label>
        <input
          id={endpointId}
          type="url"
          className="input"
          placeholder="https://s3.us-east-1.amazonaws.com"
          value={origin.endpoint}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...origin, endpoint: e.target.value })}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={bucketId}>
          Bucket{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={bucketId}
          type="text"
          className="input"
          value={origin.bucket}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...origin, bucket: e.target.value })}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={regionId}>
          Region{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
          <span className="landing-label-hint">
            <code>auto</code> for Cloudflare R2; <code>us-east-1</code> is a safe default for MinIO.
          </span>
        </label>
        <input
          id={regionId}
          type="text"
          className="input"
          placeholder="us-east-1"
          value={origin.region}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...origin, region: e.target.value })}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={accessId}>
          Access key ID{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={accessId}
          type="text"
          className="input"
          autoComplete="off"
          value={origin.accessKeyId}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...origin, accessKeyId: e.target.value })}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={secretId}>
          Secret access key{' '}
          <span className="landing-label-required" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id={secretId}
          type="password"
          className="input"
          autoComplete="off"
          value={origin.secretAccessKey}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...origin, secretAccessKey: e.target.value })}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={publicId}>
          Public URL base (optional)
          <span className="landing-label-hint">
            Used to construct the URLs written into your content. Default is the endpoint + bucket;
            override when serving through a CDN, e.g. <code>https://cdn.example.com</code>.
          </span>
        </label>
        <input
          id={publicId}
          type="url"
          className="input"
          placeholder="https://cdn.example.com"
          value={origin.publicUrlBase ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length > 0) onChange({ ...origin, publicUrlBase: v });
            else {
              const { publicUrlBase: _omit, ...rest } = origin;
              onChange(rest);
            }
          }}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={prefixId}>
          Prefix (optional)
          <span className="landing-label-hint">
            Restricts the gallery to a folder, e.g. <code>assets/</code>. Uploads land here too.
          </span>
        </label>
        <input
          id={prefixId}
          type="text"
          className="input"
          placeholder="assets/"
          value={origin.prefix ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length > 0) onChange({ ...origin, prefix: v });
            else {
              const { prefix: _omit, ...rest } = origin;
              onChange(rest);
            }
          }}
        />
      </div>

      <div className="landing-field">
        <label className="landing-label" htmlFor={pathStyleId}>
          <input
            id={pathStyleId}
            type="checkbox"
            checked={origin.forcePathStyle ?? false}
            onChange={(e) => {
              if (e.target.checked) onChange({ ...origin, forcePathStyle: true });
              else {
                const { forcePathStyle: _omit, ...rest } = origin;
                onChange(rest);
              }
            }}
          />{' '}
          Force path-style addressing
          <span className="landing-label-hint">
            Required for MinIO and most localhost-style endpoints. Leave off for AWS S3 and R2.
          </span>
        </label>
      </div>

      <div className="landing-field">
        <button
          type="button"
          className="landing-button-text"
          onClick={() => {
            void handleTest();
          }}
          disabled={testing}
        >
          {testing ? 'Testing…' : 'Test connection'}
        </button>
        {testResult && (
          <p
            className={'asset-origin-test-result' + (testResult.ok ? ' is-ok' : ' is-error')}
            role={testResult.ok ? 'status' : 'alert'}
          >
            {testResult.message}
          </p>
        )}
      </div>
    </>
  );
}

export type AssetOriginErrors = Record<string, string>;

export function validateAssetOrigin(origin: AssetOriginConfig | undefined): AssetOriginErrors {
  if (origin === undefined) return {};
  if (origin.kind === 's3') {
    const errors: AssetOriginErrors = {};
    if (!origin.endpoint) errors.endpoint = 'Endpoint is required.';
    else {
      try {
        const parsed = new URL(origin.endpoint);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          errors.endpoint = 'Endpoint must use http:// or https://.';
        }
      } catch {
        errors.endpoint = 'Endpoint is not a valid URL.';
      }
    }
    if (!origin.bucket) errors.bucket = 'Bucket is required.';
    if (!origin.region) errors.region = 'Region is required.';
    if (!origin.accessKeyId) errors.accessKeyId = 'Access key ID is required.';
    if (!origin.secretAccessKey) errors.secretAccessKey = 'Secret access key is required.';
    if (origin.publicUrlBase) {
      try {
        new URL(origin.publicUrlBase);
      } catch {
        errors.publicUrlBase = 'Public URL base is not a valid URL.';
      }
    }
    return errors;
  }
  return {};
}
